import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { FiX, FiAlertCircle, FiClock, FiTrendingUp } from 'react-icons/fi';
import { getUserCaliperEvents, UserCaliperEvent } from '../../api/admin';

interface UserCaliperDetailsProps {
  userEmail: string;
  startDate: string;
  endDate: string;
  onClose: () => void;
}

export const UserCaliperDetails: React.FC<UserCaliperDetailsProps> = ({
  userEmail,
  startDate,
  endDate,
  onClose
}) => {
  const [events, setEvents] = useState<UserCaliperEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await getUserCaliperEvents(userEmail, startDate, endDate);
        setEvents(result.events);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user events';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [userEmail, startDate, endDate]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getItemValue = (items: Array<{ type: string; value: number }>, type: string): number => {
    const item = items.find(i => i.type === type);
    return item ? item.value : 0;
  };

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] flex flex-col bg-white shadow-2xl">
        <CardContent className="p-6 flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
          {/* Header */}
          <div className="flex justify-between items-center mb-4 flex-shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Caliper Events</h2>
              <p className="text-sm text-gray-600 mt-1">
                {userEmail} ({startDate} to {endDate})
              </p>
            </div>
            <Button
              onClick={onClose}
              variant="outline"
              className="flex items-center"
            >
              <FiX className="w-5 h-5" />
            </Button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading events...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                <h3 className="text-lg font-semibold text-gray-900 mt-4">Error Loading Events</h3>
                <p className="text-gray-600 mt-2">{error}</p>
              </div>
            </div>
          )}

          {/* Events Table */}
          {!isLoading && !error && (
            <>
              {events.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-gray-600">No events found for this user in the selected date range.</p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-auto border border-gray-200 rounded-lg" style={{ minHeight: 0 }}>
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Event Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Activity Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Active Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Waste Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          XP Earned
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Questions
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mastered Units
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Track ID
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {events.map((event, index) => {
                        const activeTime = getItemValue(event.items, 'active');
                        const wasteTime = getItemValue(event.items, 'waste');
                        const xpEarned = getItemValue(event.items, 'xpEarned');
                        const totalQuestions = getItemValue(event.items, 'totalQuestions');
                        const correctQuestions = getItemValue(event.items, 'correctQuestions');
                        const masteredUnits = getItemValue(event.items, 'masteredUnits');

                        return (
                          <tr
                            key={index}
                            className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatDateTime(event.eventTime)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {event.activityType || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {activeTime > 0 ? (
                                <span className="flex items-center">
                                  <FiClock className="w-4 h-4 mr-1 text-green-600" />
                                  {Math.floor(activeTime / 60)}m {Math.round(activeTime % 60)}s
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {wasteTime > 0 ? `${Math.floor(wasteTime / 60)}m ${Math.round(wasteTime % 60)}s` : '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {xpEarned > 0 ? (
                                <span className="flex items-center text-blue-600 font-semibold">
                                  <FiTrendingUp className="w-4 h-4 mr-1" />
                                  {xpEarned.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {totalQuestions > 0 ? `${correctQuestions}/${totalQuestions}` : '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {masteredUnits > 0 ? masteredUnits : '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {event.trackId || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
